<template>
    <div class="contact-info">
        <div class="p-4">
            <h4 class="mb-1"><i class="fas fa-address-card mr-2"></i>Contact Information</h4>
            <p class="text-muted mb-4">How The Payment Option reaches you. Verified details are used for login codes, payment notices, and mailed documents.</p>

            <div v-if="!loaded" class="text-center py-5"><b-spinner variant="secondary"/></div>
            <b-alert v-else-if="loadError" show variant="danger">We couldn't load your contact information. Please refresh the page.</b-alert>

            <template v-else>
                <!-- Email + phone: each verified by a one-time code -->
                <div class="contact-row" v-for="row in rows" :key="row.field">
                    <div class="d-flex justify-content-between align-items-start">
                        <div>
                            <div class="label"><i :class="['fas', row.icon, 'mr-2']"></i>{{ row.label }} <span class="text-muted" v-if="row.optional">(optional)</span></div>
                            <div class="value" v-if="contact[row.field]">{{ display(row, contact[row.field]) }}</div>
                            <div class="value text-muted" v-else>Not on file</div>
                            <b-badge :variant="status(row).variant" class="mt-1"><i :class="['fas', status(row).icon, 'mr-1']"></i>{{ status(row).text }}</b-badge>
                        </div>
                        <b-button size="sm" variant="success" v-if="!edit[row.field].open" @click="openEdit(row)">
                            <i :class="['far', rowAction(row).icon, 'mr-2']"></i>{{ rowAction(row).label }}
                        </b-button>
                    </div>

                    <div class="edit-panel mt-3" v-if="edit[row.field].open">
                        <template v-if="!edit[row.field].sent">
                            <b-form-group :label="row.channel === 'sms' ? 'Mobile number' : 'Email address'">
                                <b-form-input v-if="row.channel === 'sms'" v-model="edit[row.field].value" v-mask="'(###) ###-####'" placeholder="(555) 555-5555" autocomplete="tel"/>
                                <b-form-input v-else type="email" v-model="edit[row.field].value" placeholder="name@example.com" autocomplete="email"/>
                            </b-form-group>
                            <p class="text-danger" v-if="edit[row.field].error"><i class="fas fa-exclamation-triangle mr-2"></i>{{ edit[row.field].error }}</p>
                            <b-button variant="success" :disabled="!canSend(row) || edit[row.field].busy" @click="send(row)"><i class="far fa-paper-plane mr-2"></i>Send Verification Code</b-button>
                            <b-button variant="light" class="ml-2" @click="closeEdit(row)">Cancel</b-button>
                        </template>

                        <template v-else>
                            <p>
                                <i class="fas fa-arrow-right mr-2"></i>A {{ row.length }}-digit code was sent to <strong>{{ display(row, edit[row.field].value) }}</strong>.
                                <span v-if="row.channel === 'email'">If you don't see it, check your spam or junk folder.</span>
                            </p>
                            <b-form-group label="Verification Code">
                                <v-otp-input
                                    :ref="'otp-' + row.field"
                                    input-classes="otp-input"
                                    separator="-"
                                    :num-inputs="row.length"
                                    :should-auto-focus="true"
                                    :is-input-num="true"
                                    @on-change="value => edit[row.field].code = value"
                                    @on-complete="value => edit[row.field].code = value"
                                />
                            </b-form-group>
                            <p class="text-danger" v-if="edit[row.field].error"><i class="fas fa-exclamation-triangle mr-2"></i>{{ edit[row.field].error }}</p>
                            <b-button variant="success" :disabled="edit[row.field].code.length < row.length || edit[row.field].busy" @click="verify(row)"><i class="far fa-check mr-2"></i>Verify</b-button>
                            <b-button variant="link" :disabled="edit[row.field].cooldown > 0 || edit[row.field].busy" @click="send(row)">
                                Resend code<span v-if="edit[row.field].cooldown > 0"> ({{ edit[row.field].cooldown }}s)</span>
                            </b-button>
                            <b-button variant="light" class="ml-2" @click="closeEdit(row)">Cancel</b-button>
                        </template>
                    </div>
                </div>

                <!-- Mailing address: verified against PostGrid, saved to the profile -->
                <div class="contact-row">
                    <div class="d-flex justify-content-between align-items-start">
                        <div>
                            <div class="label"><i class="fas fa-envelope-open-text mr-2"></i>Mailing Address</div>
                            <div class="value" v-if="hasAddress">
                                {{ contact.address.address }}<span v-if="contact.address.address_2">, {{ contact.address.address_2 }}</span><br>
                                {{ contact.address.city }}, {{ contact.address.state }} {{ contact.address.zip }}
                            </div>
                            <div class="value text-muted" v-else>Not on file</div>
                            <b-badge :variant="addressStatus.variant" class="mt-1"><i :class="['fas', addressStatus.icon, 'mr-1']"></i>{{ addressStatus.text }}</b-badge>
                        </div>
                        <b-button size="sm" variant="success" v-if="!addr.open" @click="openAddress">
                            <i :class="['far', hasAddress ? 'fa-pencil' : 'fa-plus', 'mr-2']"></i>{{ hasAddress ? 'Change' : 'Add' }}
                        </b-button>
                    </div>

                    <div class="edit-panel mt-3" v-if="addr.open">
                        <template v-if="!addr.manual">
                            <b-form-group label="Search for your address" class="address-search">
                                <b-form-input
                                    ref="addressSearch"
                                    v-model="addr.query"
                                    autocomplete="off"
                                    placeholder="Start typing your street address..."
                                    @input="onAddressInput"
                                    @keydown.native="onAddressKeydown"
                                    @blur="hideSuggestions"
                                />
                                <div class="suggestions" v-if="addr.suggestions.length">
                                    <div
                                        v-for="(s, i) in addr.suggestions"
                                        :key="i"
                                        :class="['suggestion', {active: i === addr.index}]"
                                        @mousedown.prevent="pickSuggestion(i)"
                                    >
                                        <div>{{ s.preview.address }}</div>
                                        <small class="text-muted">{{ cityStateZip(s.preview) }}</small>
                                    </div>
                                </div>
                                <small class="text-muted d-block mt-2">Leave out apartment or unit numbers here; you can add them on the next step.</small>
                            </b-form-group>
                            <p class="text-danger" v-if="addr.message"><i class="fas fa-exclamation-triangle mr-2"></i>{{ addr.message }}</p>
                            <b-button variant="link" size="sm" class="pl-0" @click="addr.manual = true">Enter address manually</b-button>
                            <b-button variant="light" class="ml-2" @click="addr.open = false">Cancel</b-button>
                        </template>

                        <template v-else>
                            <b-row>
                                <b-col md="8"><b-form-group label="Street Address"><b-form-input v-model="addr.form.address" autocomplete="address-line1"/></b-form-group></b-col>
                                <b-col md="4"><b-form-group label="Apt / Unit"><b-form-input v-model="addr.form.address_2" autocomplete="address-line2"/></b-form-group></b-col>
                            </b-row>
                            <b-row>
                                <b-col md="6"><b-form-group label="City"><b-form-input v-model="addr.form.city" autocomplete="address-level2"/></b-form-group></b-col>
                                <b-col md="2"><b-form-group label="State"><b-form-input v-model="addr.form.state" v-mask="'AA'" autocomplete="address-level1"/></b-form-group></b-col>
                                <b-col md="4"><b-form-group label="ZIP"><b-form-input v-model="addr.form.zip" v-mask="'#####-####'" autocomplete="postal-code"/></b-form-group></b-col>
                            </b-row>
                            <p :class="addr.failed ? 'text-danger' : 'text-muted'" v-if="addr.message"><i class="fas fa-exclamation-triangle mr-2" v-if="addr.failed"></i>{{ addr.message }}</p>
                            <b-form-checkbox v-if="addr.failed" v-model="addr.bypass" class="mb-3">Save this address anyway; I've checked that it's correct.</b-form-checkbox>
                            <b-button variant="success" :disabled="!addressComplete || addr.busy" @click="verifyAndSave">
                                <i class="far fa-check mr-2"></i>{{ addr.failed && addr.bypass ? 'Save Address' : 'Verify & Save' }}
                            </b-button>
                            <b-button variant="link" @click="resetAddress">Search instead</b-button>
                            <b-button variant="light" class="ml-2" @click="addr.open = false">Cancel</b-button>
                        </template>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
    import {mapActions} from 'vuex';
    import {formatPhone, postgridStreetQuery} from '@/utils/contact';

    // Same keys the sign-agreement step uses (publishable key in production)
    const POSTGRID_KEY = window.location.hostname === 'localhost' ? 'test_sk_xu8qZj7UQksafFt5JTrWmx' : 'live_pk_dgaCoQ49WfT3o8oGjmTpRo';
    const POSTGRID = 'https://api.postgrid.com/v1/addver';
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    const ROWS = [
        {field: 'email',   label: 'Primary Email',            channel: 'email', icon: 'fa-envelope',      length: 6},
        {field: 'email_2', label: 'Secondary Email',          channel: 'email', icon: 'fa-envelope-open', length: 6, optional: true},
        {field: 'phone',   label: 'Text-Message (SMS) Phone', channel: 'sms',   icon: 'fa-mobile',        length: 5}
    ];

    const blankEdit = () => ({open: false, value: '', sent: false, code: '', error: '', busy: false, cooldown: 0});
    const blankAddress = () => ({
        open: false, manual: false, query: '', suggestions: [], index: -1,
        form: {address: '', address_2: '', city: '', state: '', zip: ''},
        message: '', failed: false, bypass: false, busy: false
    });

    export default {
        name: 'ContactInfo',
        props: ['code'],
        data() {
            return {
                contact: null,
                loaded: false,
                loadError: false,
                rows: ROWS,
                edit: {email: blankEdit(), email_2: blankEdit(), phone: blankEdit()},
                addr: blankAddress(),
                timers: {}
            };
        },
        computed: {
            hasAddress() {
                const a = this.contact && this.contact.address;
                return !!(a && a.address && a.city && a.state && a.zip);
            },
            addressComplete() {
                const f = this.addr.form;
                return !!(f.address && f.city && /^[A-Za-z]{2}$/.test(f.state) && /^\d{5}(-\d{4})?$/.test(f.zip));
            },
            addressStatus() {
                if (!this.hasAddress) return {variant: 'danger', icon: 'fa-exclamation-circle', text: 'Missing'};
                if (this.contact.verified.address_postgrid) return {variant: 'success', icon: 'fa-check-circle', text: 'Verified'};
                if (this.contact.verified.address) return {variant: 'secondary', icon: 'fa-check', text: 'Saved, not verified'};
                return {variant: 'warning', icon: 'fa-exclamation-triangle', text: 'Not verified'};
            }
        },
        methods: {
            ...mapActions(['getContact', 'sendContactCode', 'verifyContactCode', 'saveContactAddress']),

            async load() {
                try {
                    this.apply(await this.getContact(this.code));
                } catch (err) {
                    this.loadError = true;
                }
                this.loaded = true;
            },
            apply(contact) {
                this.contact = contact;
                this.$emit('contact', contact);
            },
            display(row, value) {
                return row.channel === 'sms' ? formatPhone(value) : value;
            },
            // Row action button: what it says depends on whether a value is on
            // file and whether it has been verified
            rowAction(row) {
                if (!this.contact[row.field]) return {label: 'Add', icon: 'fa-plus'};
                return this.contact.verified[row.field]
                    ? {label: 'Change', icon: 'fa-pencil'}
                    : {label: 'Verify', icon: 'fa-check'};
            },
            status(row) {
                if (!this.contact[row.field]) {
                    return row.optional
                        ? {variant: 'light', icon: 'fa-minus', text: 'None'}
                        : {variant: 'danger', icon: 'fa-exclamation-circle', text: 'Missing'};
                }
                return this.contact.verified[row.field]
                    ? {variant: 'success', icon: 'fa-check-circle', text: 'Verified'}
                    : {variant: 'warning', icon: 'fa-exclamation-triangle', text: 'Not verified'};
            },

            // --- one-time code rows -------------------------------------------
            openEdit(row) {
                const current = this.contact[row.field];
                // Prefill an unverified value so verifying it is one click
                const value = current && !this.contact.verified[row.field] ? this.display(row, current) : '';
                Object.assign(this.edit[row.field], blankEdit(), {open: true, value});
            },
            closeEdit(row) {
                clearInterval(this.timers[row.field]);
                Object.assign(this.edit[row.field], blankEdit());
            },
            canSend(row) {
                const value = this.edit[row.field].value || '';
                return row.channel === 'sms' ? value.replace(/\D/g, '').length === 10 : EMAIL_RE.test(value.trim());
            },
            async send(row) {
                const e = this.edit[row.field];
                e.error = '';
                e.busy = true;
                try {
                    await this.sendContactCode({session: this.code, field: row.field, value: e.value});
                    e.sent = true;
                    e.code = '';
                    this.clearOtp(row);
                    this.startCooldown(row, 60);
                } catch (err) {
                    e.error = this.describe(err, row);
                    if (err && err.status === 429) {
                        // A code is already out; let them enter it
                        e.sent = true;
                        this.startCooldown(row, (err.data && err.data.retry_in) || 60);
                    }
                }
                e.busy = false;
            },
            async verify(row) {
                const e = this.edit[row.field];
                e.error = '';
                e.busy = true;
                try {
                    const contact = await this.verifyContactCode({session: this.code, field: row.field, value: e.value, code: e.code});
                    this.apply(contact);
                    this.closeEdit(row);
                    this.$bvToast.toast(`${row.label} verified.`, {title: 'Contact Information', variant: 'success', solid: true, autoHideDelay: 4000});
                } catch (err) {
                    e.error = this.describe(err, row);
                    e.code = '';
                    this.clearOtp(row);
                }
                e.busy = false;
            },
            describe(err, row) {
                const code = err && err.data && err.data.error;
                const what = row.channel === 'sms' ? 'number' : 'email address';
                switch (code) {
                    case 'invalid_value': return `That doesn't look like a valid ${what}.`;
                    case 'in_use': return `That ${what} is already on another account. Contact us if you think that's a mistake.`;
                    case 'too_soon': return 'Please wait a moment before requesting another code.';
                    case 'send_failed': return `We couldn't deliver the code. Check the ${what} and try again.`;
                    case 'mismatch': return 'That code didn\'t match. For security, request a new code and try again.';
                    case 'expired': return 'That code has expired. Request a new one.';
                    case 'invalid_session': return 'Your session has ended. Please log in again.';
                    default: return 'Something went wrong. Please try again.';
                }
            },
            startCooldown(row, seconds) {
                const e = this.edit[row.field];
                clearInterval(this.timers[row.field]);
                e.cooldown = seconds;
                this.timers[row.field] = setInterval(() => {
                    if (e.cooldown <= 1) {
                        e.cooldown = 0;
                        clearInterval(this.timers[row.field]);
                    } else {
                        e.cooldown--;
                    }
                }, 1000);
            },
            clearOtp(row) {
                const ref = this.$refs['otp-' + row.field];
                const otp = Array.isArray(ref) ? ref[0] : ref;
                if (otp && otp.clearInput) otp.clearInput();
            },

            // --- mailing address ----------------------------------------------
            openAddress() {
                Object.assign(this.addr, blankAddress(), {open: true});
                this.$nextTick(() => {
                    if (this.$refs.addressSearch) this.$refs.addressSearch.focus();
                });
            },
            resetAddress() {
                Object.assign(this.addr, blankAddress(), {open: true});
            },
            hideSuggestions() {
                // Let a mousedown on a suggestion land first
                setTimeout(() => { this.addr.suggestions = []; }, 150);
            },
            async onAddressInput(value) {
                const query = postgridStreetQuery(value);
                if (query.length < 3) {
                    this.addr.suggestions = [];
                    return;
                }
                try {
                    const response = await fetch(`${POSTGRID}/completions?partialStreet=${encodeURIComponent(query)}`, {headers: {'x-api-key': POSTGRID_KEY}});
                    const data = response.ok ? await response.json() : null;
                    this.addr.suggestions = (data && data.data) || [];
                    this.addr.index = this.addr.suggestions.length ? 0 : -1;
                } catch (err) {
                    this.addr.suggestions = [];
                }
            },
            onAddressKeydown(event) {
                if (!this.addr.suggestions.length) return;
                if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    this.addr.index = Math.min(this.addr.index + 1, this.addr.suggestions.length - 1);
                } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    this.addr.index = Math.max(this.addr.index - 1, 0);
                } else if (event.key === 'Enter') {
                    event.preventDefault();
                    if (this.addr.index >= 0) this.pickSuggestion(this.addr.index);
                } else if (event.key === 'Escape') {
                    this.addr.suggestions = [];
                }
            },
            async pickSuggestion(index) {
                this.addr.busy = true;
                this.addr.message = '';
                try {
                    const response = await fetch(`${POSTGRID}/completions?index=${index}`, {
                        method: 'POST',
                        headers: {'x-api-key': POSTGRID_KEY, 'Content-Type': 'application/json'},
                        body: JSON.stringify({partialStreet: postgridStreetQuery(this.addr.query)})
                    });
                    const data = response.ok ? await response.json() : null;
                    let picked = null;
                    if (data && data.data) picked = Array.isArray(data.data) ? (data.data[0] && data.data[0].address) : data.data.address;
                    if (picked) {
                        this.addr.form = {address: picked.address || '', address_2: picked.address2 || '', city: picked.city || '', state: picked.prov || '', zip: picked.pc || ''};
                        this.addr.suggestions = [];
                        this.addr.manual = true;
                        this.addr.message = 'Add an apartment or unit number if you have one, then Verify & Save.';
                    } else {
                        this.addr.message = 'Could not load that address. Try again or enter it manually.';
                    }
                } catch (err) {
                    this.addr.message = 'Could not load that address. Try again or enter it manually.';
                }
                this.addr.busy = false;
            },
            async verifyAndSave() {
                if (!this.addressComplete) return;
                this.addr.busy = true;
                this.addr.message = '';
                const f = this.addr.form;
                let verified = false;
                let detail = '';

                try {
                    const response = await fetch(`${POSTGRID}/verifications`, {
                        method: 'POST',
                        headers: {'x-api-key': POSTGRID_KEY, 'Content-Type': 'application/json'},
                        body: JSON.stringify({address: `${f.address}${f.address_2 ? ', ' + f.address_2 : ''}, ${f.city}, ${f.state} ${f.zip}`})
                    });
                    const data = response.ok ? await response.json() : null;
                    const v = data && data.data;
                    if (v && (v.status === 'verified' || v.status === 'corrected')) {
                        verified = true;
                        if (v.line1) {
                            this.addr.form = {
                                address: v.line1,
                                address_2: v.line2 || f.address_2 || '',
                                city: v.city || f.city,
                                state: v.provinceOrState || f.state,
                                zip: v.postalOrZip || f.zip
                            };
                        }
                    } else if (v && v.errors) {
                        detail = Object.values(v.errors).map(m => Array.isArray(m) ? m.join(', ') : m).join('; ');
                    }
                } catch (err) {
                    // PostGrid unreachable: fall through to the save-anyway path
                }

                if (!verified && !this.addr.bypass) {
                    this.addr.failed = true;
                    this.addr.message = detail
                        ? `We couldn't verify this address (${detail}). Correct it, or check the box to save it anyway.`
                        : 'We couldn\'t verify this address. Correct it, or check the box to save it anyway.';
                    this.addr.busy = false;
                    return;
                }

                try {
                    const contact = await this.saveContactAddress({session: this.code, ...this.addr.form, postgrid_verified: verified});
                    this.apply(contact);
                    this.addr.open = false;
                    this.$bvToast.toast(verified ? 'Mailing address verified and saved.' : 'Mailing address saved.', {title: 'Contact Information', variant: 'success', solid: true, autoHideDelay: 4000});
                } catch (err) {
                    this.addr.failed = true;
                    this.addr.message = err && err.data && err.data.error === 'invalid_address'
                        ? 'Please complete the address.'
                        : 'Could not save the address. Please try again.';
                }
                this.addr.busy = false;
            },
            cityStateZip(p) {
                return [p.city, p.prov || p.state, p.pc || p.zip].filter(Boolean).join(', ');
            }
        },
        mounted() {
            this.load();
        },
        beforeDestroy() {
            Object.values(this.timers).forEach(clearInterval);
        }
    };
</script>

<style lang="scss" scoped>
    .contact-row {
        padding: 1.25rem 0;
        border-bottom: 1px solid #eeeeee;

        &:last-child {
            border-bottom: 0;
        }
    }

    .label {
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: grey;
        margin-bottom: 0.25rem;
    }

    .value {
        font-size: 1.05rem;
    }

    .edit-panel {
        background-color: #f7f7f7;
        border-radius: 0.25rem;
        padding: 1.25rem;
    }

    .address-search {
        position: relative;
    }

    .suggestions {
        position: absolute;
        z-index: 20;
        left: 0;
        right: 0;
        background: #ffffff;
        border: 1px solid #dddddd;
        border-radius: 0.25rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        max-height: 16rem;
        overflow-y: auto;

        .suggestion {
            padding: 0.5rem 0.75rem;
            cursor: pointer;

            &:hover,
            &.active {
                background-color: #f4f4f4;
            }
        }
    }
</style>
